import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useTerminal from '../models/useTerminal'
import { checkAccess } from '../service/access'

type TerminalRouteProps = {
  onReady?: () => void
}

export default function TerminalRoute({ onReady }: TerminalRouteProps) {
  const navigate = useNavigate()
  const { serviceName } = useParams<{ serviceName: string }>()
  const sessions = useTerminal(state => state.sessions)
  const openSession = useTerminal(state => state.openSession)
  const routeSessionIdRef = useRef<string | null>(null)
  const hasSeenRouteSessionRef = useRef(false)

  const [authorized, setAuthorized] = useState(false)

  // Check access before opening session
  useEffect(() => {
    if (!serviceName) return
    let cancelled = false

    checkAccess(serviceName).then(result => {
      if (cancelled) return
      if (result?.authorized) {
        setAuthorized(true)
      } else {
        navigate(`/access/${encodeURIComponent(serviceName)}`, { replace: true })
      }
    })

    return () => { cancelled = true }
  }, [serviceName, navigate])

  // Open session only when authorized
  useEffect(() => {
    if (!serviceName || !authorized) return
    if (routeSessionIdRef.current) return

    // Reuse existing session for this service if one was already hydrated
    const existing = sessions.find(s => s.serviceName === serviceName)
    if (existing) {
      routeSessionIdRef.current = existing.id
      hasSeenRouteSessionRef.current = true
      onReady?.()
      return
    }

    onReady?.()
    const sessionId = openSession({ serviceName, mode: 'fullscreen', openedFromRoute: true })
    routeSessionIdRef.current = sessionId
    hasSeenRouteSessionRef.current = false
  }, [authorized, onReady, openSession, serviceName, sessions])

  // Navigate home when session is closed
  useEffect(() => {
    const sessionId = routeSessionIdRef.current
    if (!sessionId) return

    const exists = sessions.some(session => session.id === sessionId)
    if (exists) {
      hasSeenRouteSessionRef.current = true
      return
    }

    if (hasSeenRouteSessionRef.current) {
      routeSessionIdRef.current = null
      hasSeenRouteSessionRef.current = false
      navigate('/', { replace: true })
    }
  }, [navigate, sessions])

  return null
}
